import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Alert,
  Menu,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import Sidebar from '../../Pages/landingPage/Sidebar';
import {
  Announcement,
  Person4,
  SettingsRounded,
  TrackChanges,
  Business,
  ContactMail,
  History,
  AssignmentInd,
  Note,
  SwapHoriz,
  Phone,
  RateReview,
} from '@mui/icons-material';
import { AdminTabs } from '../AdminTabs';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import {
  getContactSubmissions,
  getContactSubmissionById,
  updateContactSubmission,
  fetchSuperAdmins,
  getContactSubmissionActivity,
} from '../../apis/Admin';
import { useTimezone } from '../../hooks/useTimezone';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: (Number(i) || 0) * 0.05,
      duration: 0.3,
      ease: 'easeOut',
    },
  }),
};

const statusColors = {
  new: { bg: '#E3F2FD', color: '#1976D2', label: 'New' },
  in_progress: { bg: '#FFF3E0', color: '#F57C00', label: 'In Progress' },
  resolved: { bg: '#E8F5E9', color: '#388E3C', label: 'Resolved' },
  archived: { bg: '#F5F5F5', color: '#616161', label: 'Archived' },
};

const thSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  py: 1,
  px: 2,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

export default function AdminContactSubmissions() {
  const { formatDate } = useTimezone();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  // eslint-disable-next-line no-unused-vars
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [editForm, setEditForm] = useState({
    status: '',
    contact_status: '',
    followup_notes: '',
    assigned_to: '',
    phone_number: '',
  });

  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const fetchSubmissions = async () => {
    if (!isSuperAdmin) return;

    setLoading(true);
    setPageError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(contactStatusFilter !== 'all' && { 
          contact_status: contactStatusFilter === 'null' ? 'null' : contactStatusFilter 
        }),
        ...(assignedToFilter !== 'all' && { assigned_to: assignedToFilter }),
        ...(fromDate && { from_date: fromDate.format('YYYY-MM-DD') }),
        ...(toDate && { to_date: toDate.format('YYYY-MM-DD') }),
        ...(search && { search }),
      };

      const response = await getContactSubmissions(params);
      
      if (response && response.submissions) {
        setSubmissions(response.submissions);
        setTotal(response.total || 0);
        setTotalPages(response.total_pages || 0);
      } else if (Array.isArray(response)) {
        setSubmissions(response);
        setTotal(response.length);
        setTotalPages(1);
      } else {
        setSubmissions([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      const errorMessage = error.message || 'Failed to fetch contact submissions';
      setPageError(errorMessage);
      toast.error(errorMessage);
      setSubmissions([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [page, statusFilter, contactStatusFilter, assignedToFilter, fromDate, toDate, search, isSuperAdmin]);

  useEffect(() => {
    const loadSuperAdmins = async () => {
      if (!isSuperAdmin) return;
      try {
        const admins = await fetchSuperAdmins();
        setSuperAdmins(admins);
      } catch (error) {
        console.error('Error fetching SuperAdmins:', error);
      }
    };
    loadSuperAdmins();
  }, [isSuperAdmin]);

  const handleView = async (submissionId) => {
    try {
      const submission = await getContactSubmissionById(submissionId);
      setSelectedSubmission(submission);
      setViewOpen(true);
      
      setLoadingActivity(true);
      try {
        const activityData = await getContactSubmissionActivity(submissionId);
        setActivityLogs(activityData.activities || []);
      } catch (activityError) {
        console.error('Error fetching activity logs:', activityError);
        setActivityLogs([]);
      } finally {
        setLoadingActivity(false);
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch submission details';
      toast.error(errorMessage);
    }
  };

  const handleEdit = async (submissionId) => {
    try {
      const submission = await getContactSubmissionById(submissionId);
      setSelectedSubmission(submission);
      setEditForm({
        status: submission.status || 'new',
        contact_status: submission.contact_status ?? '',
        followup_notes: submission.followup_notes || '',
        assigned_to: submission.assigned_to || '',
        phone_number: submission.phone_number || '',
      });
      setEditOpen(true);
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch submission details';
      toast.error(errorMessage);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSubmission) return;

    setUpdating(true);
    try {
      const updatePayload = {
        status: editForm.status,
        contact_status: editForm.contact_status === '' ? null : editForm.contact_status || null,
        followup_notes: editForm.followup_notes?.trim() || null,
        assigned_to: editForm.assigned_to || null,
        phone_number: editForm.phone_number?.trim() || null,
      };

      await updateContactSubmission(selectedSubmission.id, updatePayload);
      toast.success('Submission updated successfully');
      setEditOpen(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      const errorMessage = error.message || 'Failed to update submission';
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleQuickContactStatusUpdate = async (submissionId, newContactStatus) => {
    setUpdating(true);
    try {
      const updatePayload = {
        contact_status: newContactStatus,
      };

      await updateContactSubmission(submissionId, updatePayload);
      toast.success(
        `Contact status updated to ${
          newContactStatus === 'contacted'
            ? 'Contacted'
            : newContactStatus === 'not_interested'
            ? 'Not Interested'
            : 'New'
        }`
      );
      fetchSubmissions();
    } catch (error) {
      const errorMessage = error.message || 'Failed to update contact status';
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };


  const getStatusChip = (status) => {
    const statusConfig = statusColors[status] || statusColors.new;
    return (
      <Chip
        label={statusConfig.label}
        sx={{
          bgcolor: statusConfig.bg,
          color: statusConfig.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
        }}
      />
    );
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Sidebar />
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <Alert severity="error">Access denied. SuperAdmin role required.</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <Sidebar />
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
          }}
        >
        <AdminTabs
          items={[
            ...(isSuperAdmin
              ? [
                  {
                    to: '/admin/organizations',
                    label: 'Organizations',
                    icon: Business,
                  },
                ]
              : []),
            { to: '/admin/user', label: 'User Management', icon: Person4 },
            {
              to: '/admin/annoucement',
              label: 'Annoucement',
              icon: Announcement,
            },
            {
              to: '/admin/setting',
              label: 'Application Settings',
              icon: SettingsRounded,
            },
            {
              to: '/admin/audit-log',
              label: 'Audit Logs',
              icon: TrackChanges,
            },
            ...(isSuperAdmin
              ? [
                  {
                    to: '/admin/contact-submissions',
                    label: 'Contact Submissions',
                    icon: ContactMail,
                  },
                  {
                    to: '/admin/public-reviews',
                    label: 'Public Reviews',
                    icon: RateReview,
                  },
                ]
              : []),
          ]}
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            component={motion.div}
            sx={{
              borderRadius: '4px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              p: 0,
              mt: 1,
              mb: 3,
              position: 'relative',
              overflow: 'hidden',
              boxShadow:
                '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
            }}
          >
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2.5,
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 600,
                      color: '#111827',
                      letterSpacing: '0.03em',
                      lineHeight: 1.2,
                    }}
                  >
                    Contact Submissions
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AnimatePresence initial={false}>
                      {searchOpen ? (
                        <motion.div
                          key="searchbar"
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: 280, opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ type: 'tween', duration: 0.25 }}
                        >
                          <TextField
                            autoFocus
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setPage(1);
                            }}
                            placeholder="Search by name, email, subject, phone, or notes…"
                            size="small"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon
                                    fontSize="small"
                                    sx={{ color: '#6B7280' }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSearch('');
                                      setSearchOpen(false);
                                      setPage(1);
                                    }}
                                    edge="end"
                                  >
                                    <CloseRounded sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: 34,
                                borderRadius: '8px',
                                background: '#F8FAFC',
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="searchicon"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Tooltip title="Search contact submissions">
                            <IconButton
                              onClick={() => setSearchOpen(true)}
                              size="small"
                              sx={{
                                border: '1px solid rgba(79, 172, 254, 0.2)',
                                background: '#EEF2FF',
                                color: '#5965A6',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                transition:
                                  'transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, background-color .2s ease',
                                '&:hover': {
                                  background: '#E5E9FF',
                                  transform: 'translateY(-1px) scale(1.02)',
                                  boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                                },
                              }}
                            >
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                </Box>
              </motion.div>

              <Box sx={{ display: 'flex', p: 2, gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                      setPage(1);
                    }}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                  <DatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => {
                      setToDate(newValue);
                      setPage(1);
                    }}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </LocalizationProvider>
                <FormControl size="small" sx={{ minWidth: 160, flexShrink: 0 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    label="Status"
                    IconComponent={KeyboardArrowDownRounded}
                    sx={{ borderRadius: '10px' }}
                  >
                    {[
                      { v: 'all', label: 'All' },
                      { v: 'new', label: 'New' },
                      { v: 'in_progress', label: 'In Progress' },
                      { v: 'resolved', label: 'Resolved' },
                      { v: 'archived', label: 'Archived' },
                    ].map((opt) => (
                      <MenuItem key={opt.v} value={opt.v}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Contact Status</InputLabel>
                  <Select
                    value={contactStatusFilter}
                    onChange={(e) => {
                      setContactStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    label="Contact Status"
                    IconComponent={KeyboardArrowDownRounded}
                    sx={{ borderRadius: '10px' }}
                  >
                    {[
                      { v: 'all', label: 'All' },
                      { v: 'null', label: 'Not Set' },
                      { v: 'contacted', label: 'Contacted' },
                      { v: 'not_interested', label: 'Not Interested' },
                    ].map((opt) => (
                      <MenuItem key={opt.v} value={opt.v}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Assigned To</InputLabel>
                  <Select
                    value={assignedToFilter}
                    onChange={(e) => {
                      setAssignedToFilter(e.target.value);
                      setPage(1);
                    }}
                    label="Assigned To"
                    IconComponent={KeyboardArrowDownRounded}
                    sx={{ borderRadius: '10px' }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="unassigned">Unassigned</MenuItem>
                    {superAdmins.map((admin) => (
                      <MenuItem key={admin.user_id} value={admin.user_id}>
                        {admin.username || admin.name || admin.email || admin.user_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {pageError && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="error" onClose={() => setPageError(null)}>
                    {pageError}
                  </Alert>
                </Box>
              )}

              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 4,
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer
                  sx={{ overflow: 'hidden', paddingBottom: '1rem' }}
                >
                  <Table
                    size="small"
                    sx={{
                      width: '100%',
                      '& .MuiTableCell-root': {
                        py: 0.5,
                        px: 2,
                        borderBottom: '1px solid #e5e7eb',
                      },
                      '& thead .MuiTableCell-root': { py: 0.5, px: 1.25 },
                      '& .MuiTableRow-root': { height: 44 },
                    }}
                  >
                    <TableHead sx={{ backgroundColor: '#5965A6' }}>
                      <TableRow
                        sx={{
                          '&:hover': { backgroundColor: '#4C5B9A' },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell sx={thSx}>Sr No</TableCell>
                        <TableCell sx={thSx}>Name</TableCell>
                        <TableCell sx={thSx}>Email</TableCell>
                        <TableCell sx={thSx}>Status</TableCell>
                        <TableCell sx={thSx}>Contact Status</TableCell>
                        <TableCell sx={thSx}>Assigned To</TableCell>
                        <TableCell sx={thSx}>Date</TableCell>
                        <TableCell sx={thSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {submissions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: '0.95rem',
                                  mb: 1,
                                }}
                              >
                                No contact submissions found.
                              </Typography>
                              <Typography
                                sx={{ color: '#94a3b8', fontSize: '0.85rem' }}
                              >
                                Contact form submissions will appear here.
                              </Typography>
                            </motion.div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <AnimatePresence>
                          {submissions.map((submission, index) => (
                          <React.Fragment key={submission.id}>
                          <TableRow
                            component={motion.tr}
                            custom={index}
                            variants={rowVariants}
                            initial="hidden"
                            animate="visible"
                            sx={{
                              borderBottom: '1px solid #F3F4F6',
                              backgroundColor:
                                index % 2 === 1 ? '#F3F5F7' : '#ffffff',
                              '&:hover': {
                                backgroundColor: '#EEF2FF',
                                transition: 'background-color 0.2s ease',
                              },
                              '& td': {
                                fontSize: '0.875rem',
                              },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setExpandedRows((prev) => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(submission.id)) {
                                        newSet.delete(submission.id);
                                      } else {
                                        newSet.add(submission.id);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  sx={{
                                    padding: '2px',
                                    color: '#5965A6',
                                    '&:hover': {
                                      backgroundColor: 'rgba(89, 101, 166, 0.08)',
                                    },
                                  }}
                                >
                                  {expandedRows.has(submission.id) ? (
                                    <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                                <Typography sx={{ color: '#374151' }}>
                                  {index + 1 + (page - 1) * perPage}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  color: '#111827',
                                }}
                              >
                                {submission.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ color: '#6b7280' }}>
                                {submission.email}
                              </Typography>
                            </TableCell>
                            <TableCell>{getStatusChip(submission.status)}</TableCell>
                            <TableCell>
                              {submission.contact_status ? (
                                <Chip
                                  label={
                                    submission.contact_status === 'contacted'
                                      ? 'Contacted'
                                      : submission.contact_status === 'not_interested'
                                      ? 'Not Interested'
                                      : submission.contact_status
                                  }
                                  sx={{
                                    bgcolor:
                                      submission.contact_status === 'contacted'
                                        ? '#E8F5E9'
                                        : submission.contact_status === 'not_interested'
                                        ? '#FFEBEE'
                                        : '#F5F5F5',
                                    color:
                                      submission.contact_status === 'contacted'
                                        ? '#388E3C'
                                        : submission.contact_status === 'not_interested'
                                        ? '#D32F2F'
                                        : '#616161',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24,
                                  }}
                                />
                              ) : (
                                <Typography sx={{ color: '#9ca3af', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                                  New
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {submission.assigned_to ? (
                                <Typography sx={{ color: '#374151', fontSize: '0.8125rem', fontWeight: 500 }}>
                                  {(() => {
                                    const assignedUser = superAdmins.find(
                                      (admin) => admin.user_id === submission.assigned_to
                                    );
                                    return assignedUser
                                      ? assignedUser.username || assignedUser.name || assignedUser.email || assignedUser.user_id
                                      : submission.assigned_to;
                                  })()}
                                </Typography>
                              ) : (
                                <Typography sx={{ color: '#9ca3af', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                                  Unassigned
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                                {formatDate(submission.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start' }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleView(submission.id)}
                                    sx={{
                                      color: '#5965A6',
                                      '&:hover': { bgcolor: '#EEF2FF' },
                                    }}
                                  >
                                    <VisibilityRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(submission.id)}
                                    sx={{
                                      color: '#5965A6',
                                      '&:hover': { bgcolor: '#EEF2FF' },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Quick Actions">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      setAnchorEl(e.currentTarget);
                                      setSelectedRowId(submission.id);
                                    }}
                                    sx={{
                                      color: '#5965A6',
                                      '&:hover': { bgcolor: '#EEF2FF' },
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ display: expandedRows.has(submission.id) ? 'table-row' : 'none' }}>
                            <TableCell
                              sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }}
                              colSpan={8}
                            >
                              <Collapse in={expandedRows.has(submission.id)} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    margin: 2,
                                    padding: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: '0.75rem',
                                          color: '#6b7280',
                                          mb: 0.5,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          fontWeight: 600,
                                        }}
                                      >
                                        Phone Number
                                      </Typography>
                                      <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                        {submission.phone_number || 'Not provided'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: '0.75rem',
                                          color: '#6b7280',
                                          mb: 0.5,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          fontWeight: 600,
                                        }}
                                      >
                                        Subject
                                      </Typography>
                                      <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                        {submission.subject || 'No subject'}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ gridColumn: 'span 2' }}>
                                      <Typography
                                        sx={{
                                          fontSize: '0.75rem',
                                          color: '#6b7280',
                                          mb: 0.5,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          fontWeight: 600,
                                        }}
                                      >
                                        Message
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.875rem',
                                          color: '#111827',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {submission.message}
                                      </Typography>
                                    </Box>
                                    {submission.followup_notes && (
                                      <Box sx={{ gridColumn: 'span 2' }}>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontWeight: 600,
                                          }}
                                        >
                                          Follow-up Notes
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: '0.875rem',
                                            color: '#111827',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          {submission.followup_notes}
                                        </Typography>
                                      </Box>
                                    )}
                                    {submission.last_activity && (
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontWeight: 600,
                                          }}
                                        >
                                          Last Activity
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#111827', mb: 0.25 }}>
                                          {submission.last_activity}
                                        </Typography>
                                        {submission.last_activity_time && (
                                          <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                            {formatDate(submission.last_activity_time)}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                    {submission.contact_status === 'not_interested' && submission.not_interested_date && (
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontWeight: 600,
                                          }}
                                        >
                                          Not Interested Date
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                          {formatDate(submission.not_interested_date)}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                          </React.Fragment>
                          ))}
                        </AnimatePresence>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!loading && totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 2,
                    borderTop: '1px solid #e2e8f0',
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              )}
            </Box>
          </motion.div>
        </Box>
      </Box>

      <Dialog
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedSubmission(null);
          setActivityLogs([]);
        }}
        maxWidth="md"
        fullWidth
      >
          <DialogTitle>
            <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              Contact Submission Details
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedSubmission && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Name
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                    {selectedSubmission.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Email
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                    {selectedSubmission.email}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Phone Number
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                    {selectedSubmission.phone_number || 'Not provided'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Subject
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                    {selectedSubmission.subject || 'No subject'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Message
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#111827',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedSubmission.message}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Status
                  </Typography>
                  {getStatusChip(selectedSubmission.status)}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Contact Status
                  </Typography>
                  {selectedSubmission.contact_status ? (
                    <Chip
                      label={
                        selectedSubmission.contact_status === 'contacted'
                          ? 'Contacted'
                          : selectedSubmission.contact_status === 'not_interested'
                          ? 'Not Interested'
                          : selectedSubmission.contact_status
                      }
                      sx={{
                        bgcolor:
                          selectedSubmission.contact_status === 'contacted'
                            ? '#E8F5E9'
                            : selectedSubmission.contact_status === 'not_interested'
                            ? '#FFEBEE'
                            : '#F5F5F5',
                        color:
                          selectedSubmission.contact_status === 'contacted'
                            ? '#388E3C'
                            : selectedSubmission.contact_status === 'not_interested'
                            ? '#D32F2F'
                            : '#616161',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: '#9ca3af', fontStyle: 'italic' }}>
                      New
                    </Typography>
                  )}
                  {selectedSubmission.contact_status === 'not_interested' && selectedSubmission.not_interested_date && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.5 }}>
                      Marked on: {formatDate(selectedSubmission.not_interested_date)}
                    </Typography>
                  )}
                </Box>
                {selectedSubmission.assigned_to && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Assigned To
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                      {(() => {
                        const assignedUser = superAdmins.find(
                          (admin) => admin.user_id === selectedSubmission.assigned_to
                        );
                        return assignedUser
                          ? `${assignedUser.username || assignedUser.name || assignedUser.email || assignedUser.user_id}`
                          : selectedSubmission.assigned_to;
                      })()}
                    </Typography>
                  </Box>
                )}
                {selectedSubmission.followup_notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Follow-up Notes
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#111827',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {selectedSubmission.followup_notes}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Submitted
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                    {formatDate(selectedSubmission.created_at)}
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <History sx={{ fontSize: '1.125rem' }} />
                    Activity History
                  </Typography>
                  {loadingActivity ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : activityLogs.length === 0 ? (
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontStyle: 'italic',
                        py: 2,
                      }}
                    >
                      No activity recorded yet
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {activityLogs.map((activity, index) => {
                        const getActivityIcon = () => {
                          if (activity.description.includes('Assigned') || activity.description.includes('Reassigned') || activity.description.includes('Unassigned')) {
                            return <AssignmentInd sx={{ fontSize: '1rem', color: '#6366f1' }} />;
                          } else if (activity.description.includes('Status')) {
                            return <SwapHoriz sx={{ fontSize: '1rem', color: '#8b5cf6' }} />;
                          } else if (activity.description.includes('notes')) {
                            return <Note sx={{ fontSize: '1rem', color: '#10b981' }} />;
                          } else if (activity.description.includes('Phone number')) {
                            return <Phone sx={{ fontSize: '1rem', color: '#f59e0b' }} />;
                          } else {
                            return <History sx={{ fontSize: '1rem', color: '#6b7280' }} />;
                          }
                        };

                        return (
                          <Box
                            key={activity.audit_id}
                            sx={{
                              display: 'flex',
                              gap: 2,
                              position: 'relative',
                              pl: 2,
                            }}
                          >
                            {index < activityLogs.length - 1 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: '0.5rem',
                                  top: '1.5rem',
                                  bottom: '-0.5rem',
                                  width: '2px',
                                  bgcolor: '#e5e7eb',
                                }}
                              />
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                bgcolor: '#f3f4f6',
                                flexShrink: 0,
                                zIndex: 1,
                              }}
                            >
                              {getActivityIcon()}
                            </Box>
                            <Box sx={{ flex: 1, pb: index < activityLogs.length - 1 ? 2 : 0 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.875rem',
                                  color: '#111827',
                                  fontWeight: 500,
                                  mb: 0.5,
                                }}
                              >
                                {activity.description}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  mb: 0.25,
                                }}
                              >
                                by {activity.performed_by}
                                {activity.performed_by_email && ` (${activity.performed_by_email})`}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#9ca3af',
                                }}
                              >
                                {formatDate(activity.timestamp)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewOpen(false);
              setSelectedSubmission(null);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            setSelectedSubmission(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              Edit Contact Submission
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedSubmission && (
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm({ ...editForm, status: e.target.value })
                    }
                    label="Status"
                  >
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Contact Status</InputLabel>
                  <Select
                    value={editForm.contact_status ?? ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, contact_status: e.target.value })
                    }
                    label="Contact Status"
                  >
                    <MenuItem value="">New</MenuItem>
                    <MenuItem value="contacted">Contacted</MenuItem>
                    <MenuItem value="not_interested">Not Interested</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Follow-up Notes"
                  value={editForm.followup_notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, followup_notes: e.target.value })
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editForm.phone_number}
                  onChange={(e) =>
                    setEditForm({ ...editForm, phone_number: e.target.value })
                  }
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={editForm.assigned_to}
                    onChange={(e) =>
                      setEditForm({ ...editForm, assigned_to: e.target.value })
                    }
                    label="Assigned To"
                  >
                    <MenuItem value="">
                      <em>None (Unassign)</em>
                    </MenuItem>
                    {superAdmins.map((admin) => (
                      <MenuItem key={admin.user_id} value={admin.user_id}>
                        {admin.username || admin.name || admin.email || admin.user_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEditOpen(false);
                setSelectedSubmission(null);
              }}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              variant="contained"
              disabled={updating}
            >
              {updating ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setSelectedRowId(null);
          }}
          PaperProps={{
            sx: {
              mt: 0.5,
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
              minWidth: 200,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              if (selectedRowId) {
                handleQuickContactStatusUpdate(selectedRowId, 'contacted');
              }
              setAnchorEl(null);
              setSelectedRowId(null);
            }}
            disabled={updating}
            sx={{
              fontSize: '0.875rem',
              py: 1,
              '&:hover': { backgroundColor: '#F3F6FF' },
            }}
          >
            <PhoneCallbackIcon sx={{ fontSize: '1rem', mr: 1.5, color: '#388E3C' }} />
            Mark as Contacted
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedRowId) {
                handleQuickContactStatusUpdate(selectedRowId, 'not_interested');
              }
              setAnchorEl(null);
              setSelectedRowId(null);
            }}
            disabled={updating}
            sx={{
              fontSize: '0.875rem',
              py: 1,
              '&:hover': { backgroundColor: '#F3F6FF' },
            }}
          >
            <CancelIcon sx={{ fontSize: '1rem', mr: 1.5, color: '#D32F2F' }} />
            Mark as Not Interested
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedRowId) {
                handleQuickContactStatusUpdate(selectedRowId, null);
              }
              setAnchorEl(null);
              setSelectedRowId(null);
            }}
            disabled={updating}
            sx={{
              fontSize: '0.875rem',
              py: 1,
              '&:hover': { backgroundColor: '#F3F6FF' },
            }}
          >
            <RefreshIcon sx={{ fontSize: '1rem', mr: 1.5, color: '#616161' }} />
            Reset Contact Status
          </MenuItem>
        </Menu>

    </>
  );
}

